import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Activity,
  BookOpen,
  BriefcaseBusiness,
  ChevronDown,
  ChevronRight,
  FileText,
  Globe,
  Lock,
  type LucideIcon,
  Map,
  MapPin,
  Navigation,
  Settings,
  UserRound,
  UserRoundPlus,
  UsersRound,
} from "lucide-react";
import type { ReactNode } from "react";

import { DashboardProfile } from "@/components/dashboard-profile";
import { DashboardStats } from "@/components/dashboard-summary";
import { Card, CardBody, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Menu, menuItemClass } from "@/components/ui/menu";
import { Page, PageHeader } from "@/components/ui/page";
import { cn } from "@/utils/utils";
import type { ComingSoonSlug } from "@/pages/ComingSoonPage";
import type { TranslationKey } from "@/constants/i18n";
import { Tooltip } from "@/components/ui/tooltip";
import { type Gate, areaGate, notHidden, pageGate } from "@/constants/nav-permissions";
import { can, hiddenFrom } from "@/utils/permissions";
import { profileQuery } from "@/services/queries";
import { useTranslation } from "@/store/locale";

/** A tile leads to a real route, or to that section's coming-soon page. */
interface Tile {
  icon: LucideIcon;
  title: TranslationKey;
  text: TranslationKey;
  /**
   * The codenames the tile's screen is guarded by; without one of them the tile
   * is restricted. A list because some screens open on any of several — the
   * Configuration rail is nine sections and holding one of them is enough.
   */
  perm?: string | readonly string[];
  /**
   * The nav map's own answer for this screen, where the tile leads to one.
   *
   * It carries both questions — may the records be read, and has the screen
   * been hidden — so a tile closes for exactly the reasons its sidebar row
   * disappears. Without it the dashboard became the way around the thing that
   * hid the row.
   */
  gate?: Gate;
  to?:
    | "/provinces"
    | "/cities"
    | "/barangays"
    | "/configuration"
    | "/employees"
    | "/employee-activity-log"
    | "/member-activity-log"
    | "/yellow-page"
    | "/non-members"
    | "/member-population"
    | "/members";
  slug?: ComingSoonSlug;
  /** More than one destination: the tile drops a menu instead of navigating. */
  options?: { tier: MembershipTier; title: TranslationKey; perm: string }[];
}

/**
 * The four memberships a person can be enrolled into. The enrolment page reads
 * the same four off `?tier=` (`MemberNewPage`'s `TIERS`), and asking here is
 * what lets it open on the right form instead of on a question.
 */
type MembershipTier = "member" | "affiliates" | "employee" | "complimentary";

const TIERS: { tier: MembershipTier; title: TranslationKey; perm: string }[] = [
  { tier: "member", title: "mem.member", perm: "add__member" },
  { tier: "affiliates", title: "mem.affiliates", perm: "add__member_affiliates" },
  { tier: "employee", title: "mem.employee", perm: "add__member_employee" },
  { tier: "complimentary", title: "mem.complimentary", perm: "add__member_complimentary" },
];

/** The four things people come here to start, in the order they are reached for. */
const QUICK: Tile[] = [
  {
    icon: UserRoundPlus,
    title: "dash.addMember",
    // Any one of the four opens the tile; the menu below then lists only the
    // ones this person may use. Left at `add__member` it asked for the
    // ordinary tier alone, so somebody given affiliates and nothing else
    // found no tile at all — and everybody else was offered four enrolments
    // where they could make one.
    perm: TIERS.map((tier) => tier.perm),
    // The hiding half only. Enrolling asks for `add__`, not for the right to
    // browse the list — a clerk who may take somebody on but not read the
    // members still has an Add Member to press — while a Members screen kept
    // out of their interface takes this with it.
    gate: notHidden("members.list"),
    text: "dash.addMemberText",
    options: TIERS,
  },
  {
    icon: UsersRound,
    title: "dash.members",
    perm: "view__member",
    gate: areaGate("members"),
    text: "dash.membersText",
    to: "/members",
  },
  {
    icon: BriefcaseBusiness,
    title: "dash.employees",
    perm: "view__user",
    gate: areaGate("employees"),
    text: "dash.employeesText",
    to: "/employees",
  },
  {
    icon: UserRound,
    title: "dash.nonMember",
    perm: "view__non_member",
    gate: areaGate("non-members"),
    text: "dash.nonMemberText",
    to: "/non-members",
  },
];

const LOCATION: Tile[] = [
  {
    icon: Globe,
    title: "dash.province",
    perm: "view__province",
    gate: pageGate("provinces"),
    text: "dash.provinceText",
    to: "/provinces",
  },
  {
    icon: MapPin,
    title: "dash.city",
    perm: "view__city",
    gate: pageGate("cities"),
    text: "dash.cityText",
    to: "/cities",
  },
  {
    icon: Navigation,
    title: "dash.barangay",
    perm: "view__barangay",
    gate: pageGate("barangays"),
    text: "dash.barangayText",
    to: "/barangays",
  },
  {
    icon: Activity,
    title: "dash.employeeLog",
    perm: "view__employee_activity_log",
    gate: pageGate("employees.log"),
    text: "dash.employeeLogText",
    to: "/employee-activity-log",
  },
  {
    icon: FileText,
    title: "dash.memberLog",
    perm: "view__member_log",
    gate: pageGate("members.log"),
    text: "dash.memberLogText",
    to: "/member-activity-log",
  },
];

const WORKSPACE: Tile[] = [
  {
    icon: BookOpen,
    title: "dash.yellowPage",
    perm: "view__yellow_page",
    gate: areaGate("yellow-page"),
    text: "dash.yellowPageText",
    to: "/yellow-page",
  },
  // "Coverage & population" is the member-population map, which shipped: the
  // tile pointed at a coming-soon stub for a screen that is already in the
  // menu. Club Recommendation is the maps group's other page and is reached
  // from the sidebar — a tile leads to one destination.
  {
    icon: Map,
    title: "dash.maps",
    perm: "view__member_population_map",
    gate: pageGate("member-population"),
    text: "dash.mapsText",
    to: "/member-population",
  },
  {
    icon: Settings,
    title: "dash.configuration",
    text: "dash.configurationText",
    // Was the one tile with no codename at all, so it stayed lit for everybody
    // while the sidebar — reading the same map — hid Configuration entirely.
    // The two now answer to the same list: any one section of the rail opens it.
    gate: areaGate("configuration"),
    to: "/configuration",
  },
];

function DashboardPage() {
  const t = useTranslation();

  return (
    <Page>
      {/* The line above the title is a sentence, not a label, so it drops the
          eyebrow's caps and keeps only its position. */}
      <PageHeader
        eyebrow={
          <span className="text-callout font-normal tracking-normal normal-case">
            {t("dash.kicker")}
          </span>
        }
        title={t("dash.title")}
      />

      <DashboardStats />

      {/* Three columns at desk width — actions, then places and audit trails,
          then the person and their modules. Below `xl` the right column takes
          the full row and splits in two; on a phone the whole thing is one
          column in this same order. The columns stretch to one height: three
          cards ending at three different depths reads as three unfinished
          columns rather than one row. */}
      <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
        <TileCard
          icon={UserRoundPlus}
          title={t("dash.quick")}
          description={t("dash.quickText")}
          tiles={QUICK}
        />
        <TileCard
          icon={MapPin}
          title={t("dash.location")}
          description={t("dash.locationText")}
          tiles={LOCATION}
        />
        <div className="grid gap-4 lg:col-span-2 lg:grid-cols-2 xl:col-span-1 xl:grid-cols-1 xl:grid-rows-[auto_1fr]">
          <DashboardProfile />
          {/* Tiles fit themselves to whatever width the column ends up with:
              two across when this sits under the other two cards, one across
              when it is the third column or a phone. */}
          <TileCard
            icon={BookOpen}
            title={t("dash.workspace")}
            description={t("dash.workspaceText")}
            tiles={WORKSPACE}
            columns="grid-cols-[repeat(auto-fill,minmax(13rem,1fr))]"
          />
        </div>
      </div>
    </Page>
  );
}

/** One group of destinations: a titled card with its own icon, and its tiles. */
function TileCard({
  icon: Icon,
  title,
  description,
  tiles,
  columns,
}: {
  icon: LucideIcon;
  title: ReactNode;
  description: ReactNode;
  tiles: Tile[];
  /** Grid template for the tiles; one column unless the card has the width. */
  columns?: string;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="min-w-0">
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
        <span className="bg-accent-soft text-accent grid size-9 shrink-0 place-items-center rounded-[10px]">
          <Icon className="size-4.5" aria-hidden />
        </span>
      </CardHeader>
      <CardBody className={cn("grid gap-2.5", columns)}>
        {tiles.map((tile) => (
          <TileRow key={tile.title} {...tile} />
        ))}
      </CardBody>
    </Card>
  );
}

/** Permission decides whether a tile is a door or a closed one, never whether it exists. */
function useAllowed(perm?: string | readonly string[], gate?: Gate) {
  const { data: profile } = useQuery(profileQuery());
  const codenames = perm === undefined ? [] : typeof perm === "string" ? [perm] : perm;
  // Open until the profile answers: a tile is a door, and a closed door is a
  // judgement, so it waits for the facts before closing.
  if (!profile) return true;
  const readable = codenames.length === 0 || can(profile, ...codenames);
  // `hiddenFrom`, not `can`: `can` says yes to a superuser for every codename
  // there is, which would close every tile on the dashboard of the one person
  // meant to have all of them.
  const shown =
    !gate ||
    gate(
      (...names) => can(profile, ...names),
      (...names) => hiddenFrom(profile, ...names),
    );
  return readable && shown;
}

function TileRow({ icon: Icon, title, text, to, slug, perm, gate, options }: Tile) {
  const t = useTranslation();
  const { data: profile } = useQuery(profileQuery());
  const allowed = useAllowed(perm, gate);
  // Only the destinations this person may reach. A menu of four enrolments
  // where they may make one is three refusals waiting to happen — and the
  // tile's own codenames are any-of, so the tile can be open while some of
  // what is under it is not.
  //
  // Open until the profile answers, for the same reason the tile is: a closed
  // door is a judgement, and it waits for the facts.
  const offered = options?.filter((option) => !profile || can(profile, option.perm));
  const base =
    "rounded-[12px] border border-line flex min-w-0 items-center gap-3 p-3 transition-[color,background-color,border-color,transform] duration-[var(--duration-fast)] ease-[var(--ease-out-soft)]";
  const body = (
    <>
      <span
        className={cn(
          "grid size-9.5 shrink-0 place-items-center rounded-[10px]",
          allowed ? "bg-accent-soft text-accent" : "bg-surface-inset text-fg-subtle",
        )}
      >
        {allowed ? (
          <Icon className="size-4.5" aria-hidden />
        ) : (
          <Lock className="size-4.5" aria-hidden />
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="text-heading text-callout block truncate font-semibold">
          {t(title)}
        </span>
        <span className="text-fg-muted text-subhead mt-0.5 block truncate">
          {allowed ? t(text) : t("dash.restricted")}
        </span>
      </span>
      {offered?.length ? (
        <ChevronDown
          className="text-fg-subtle size-4 shrink-0 transition-transform group-open:rotate-180"
          aria-hidden
        />
      ) : (
        <ChevronRight className="text-fg-subtle size-4 shrink-0" aria-hidden />
      )}
    </>
  );
  if (!allowed) {
    // `w-full` on the wrapper as well as the row: `Tooltip` is an `inline-flex`
    // span, which shrinks to its content, so without it a shut row stops short
    // of the open one above and reads as a different kind of thing rather than
    // the same row locked.
    //
    // The app's own tooltip rather than the browser's `title`: a native one
    // waits a second, draws in the system's font at the pointer, and never
    // appears at all on a touch screen. The row already says "Restricted for
    // your role"; hovering says why, in the same voice as every other
    // explanation on the screen.
    return (
      <Tooltip label={t("dash.restrictedHint")} side="bottom" className="w-full">
        <div
          aria-disabled
          className={cn(base, "w-full", "bg-surface-inset cursor-not-allowed opacity-60")}
        >
          {body}
        </div>
      </Tooltip>
    );
  }
  const props = {
    className: cn(
      base,
      "hover:border-accent hover:bg-accent-soft hover:-translate-y-px hover:shadow-[var(--shadow-lift)]",
    ),
  };
  // A tile with more than one destination asks which one before it goes. The
  // menu is the width of the tile it drops out of, so it reads as that tile
  // opening rather than as a popover that happens to be nearby.
  if (offered?.length) {
    return (
      <Menu
        align="start"
        width="w-full"
        className="min-w-0"
        trigger={
          <span className={cn(props.className, "group-open:border-accent")}>{body}</span>
        }
      >
        {offered.map((option) => (
          <Link
            key={option.tier}
            to={`/members/new?tier=${option.tier}`}
            className={menuItemClass()}
          >
            {t(option.title)}
          </Link>
        ))}
      </Menu>
    );
  }
  return (
    <Link to={to ?? `/${slug ?? ""}`} {...props}>
      {body}
    </Link>
  );
}

export default DashboardPage;
